import { Button, Card } from '@cmsgov/ds-medicare-gov'
import classNames from 'classnames'
import { MyLink } from 'components/MyLink'
import { ConnectedAppContactInfo, ConnectedAppContactInfoType } from '@cms-max/medicare-api-sdk'
import { useTranslation } from 'react-i18next'
import classes from './ConnectedAppsCard.module.scss'
import { useEvent } from 'hooks/useEvent'

export interface ConnectedAppsCardProps {
  name: string,
  contacts: ConnectedAppContactInfo[] | undefined,
  policyUri: string,
  termsOfServiceUri: string,
  logoUri?: string,
  handleStopSharingClick: () => void,
  id: number
}

export const ConnectedAppsCard: React.FC<ConnectedAppsCardProps> = ({
  name: appName,
  contacts,
  policyUri,
  termsOfServiceUri,
  logoUri,
  handleStopSharingClick,
  id
}) => {
  const { t } = useTranslation('connectedApps')
  const middleSectionClassNames = classNames('my-u-flex-grow-1', 'my-u-p--0', {
    'my-u-sm-px--12': logoUri
  })
  const { fireEvent } = useEvent('connectedApps')

  return (
    <Card className={`${classes.card} ds-u-margin-bottom--3`}>
      <div className={`ds-u-display--flex ds-u-flex-direction--column ds-u-sm-flex-direction--row my-u-p--12 ${classes.connectedAppsCardWrapper}`}>
        {logoUri && (
          <div>
            <img
              className={classes.logo}
              src={logoUri}
              alt={t('connectedAppLogoAltText', { appName })}
            />
          </div>
        )}
        <div className={middleSectionClassNames}>
          <dl>
            <dt>{t('appName')}</dt>
            <dd className='ds-u-word-break my-u-word-break--all'>{ appName }</dd>
            {contacts?.length ? <dt>{t('contactAppOwner')}</dt> : ''}
            <dd>
              <ul>
                {contacts?.map((contact, index) => {
                  let contactUrlPrefix = ''
                  if (contact.type === ConnectedAppContactInfoType.Email) {
                    contactUrlPrefix = 'mailto:'
                  }

                  switch (contact.type) {
                    case ConnectedAppContactInfoType.Email:
                      return (
                        <li key={index}>
                          <MyLink
                            className='ds-u-word-break my-u-word-break--all'
                            href={`${contactUrlPrefix}${contact.value}`}
                          >
                            {contact.value}
                          </MyLink>
                        </li>
                      )
                    case ConnectedAppContactInfoType.Phone:
                    case ConnectedAppContactInfoType.Default:
                    default:
                      return (
                        <li
                          key={index}
                          className='my-u-m--0 ds-u-word-break my-u-word-break--all'
                        >
                          {contact.value}
                        </li>
                      )
                  }
                })}
              </ul>
            </dd>
          </dl>
          <div className='my-u-pt--4'>
            {policyUri &&
              <MyLink
                className={`ds-u-display--inline-block ${termsOfServiceUri && 'my-u-mr--16'}`}
                href={policyUri}
                aria-label={t('privacyLink.ariaLabel', { appName })}
                onClick={(params) => {
                  fireEvent('accountsettings_connectedappsprivacy_click', params)
                }}
              >
                {t('privacyLink.label')}
              </MyLink>}
            {termsOfServiceUri &&
              <MyLink
                className='ds-u-display--inline-block'
                href={termsOfServiceUri}
                aria-label={t('termsOfServiceLink.ariaLabel', { appName })}
                onClick={(params) => {
                  fireEvent('accountsettings_connectedappstermsofservice_click', params)
                }}
              >
                {t('termsOfServiceLink.label')}
              </MyLink>}
          </div>
        </div>
        <div className={`my-u-pt--8 my-u-sm-pt--0 ${classes.stopSharingButtonWrapper}`}>
          <Button
            onClick={() => {
              fireEvent('accountsettings_connectedappsstopsharing_click', { appName })
              handleStopSharingClick()
            }}
            className='ds-c-button--solid'
            aria-label={t('stopSharingButton.ariaLabel', { appName })}
          >
            {t('stopSharingButton.label')}
          </Button>
        </div>
      </div>
    </Card>
  )
}
